import { useState } from 'react'
import type { ReactNode } from 'react'
import type { components } from '@joyful/openapi/types.gen'
import type { CompositionContextValue } from '@joyful/utils'
import { createNonNullableContext } from '@joyful/utils'
import { AnnouncementsMenuHintProvider } from '../AnnouncementsMenuHintContext'
import { AnnouncementDetailDialog } from './AnnouncementDetailDialog'
import { HelpAnnouncementsDropdownMenuItem } from './HelpAnnouncementsDropdownMenuItem'
import { UnseenAnnouncementPanel } from './UnseenAnnouncementPanel'

type Announcement = components['schemas']['AnnouncementRead']

interface NavbarAnnouncementsState {
  selectedAnnouncement: Announcement | null
}

interface NavbarAnnouncementsActions {
  clearAnnouncement: () => void
  selectAnnouncement: (announcement: Announcement) => void
}

export type NavbarAnnouncementsContextValue = CompositionContextValue<
  NavbarAnnouncementsState,
  NavbarAnnouncementsActions
>

const NavbarAnnouncementsContext =
  createNonNullableContext<NavbarAnnouncementsContextValue>(
    'NavbarAnnouncementsContext',
  )

export const useNavbarAnnouncementsContext =
  NavbarAnnouncementsContext.useRequiredContext

export interface NavbarAnnouncementsProps {
  children: ReactNode
}

export function NavbarAnnouncements({ children }: NavbarAnnouncementsProps) {
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<Announcement | null>(null)

  const selectAnnouncement = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement)
  }

  const clearAnnouncement = () => {
    setSelectedAnnouncement(null)
  }

  return (
    <AnnouncementsMenuHintProvider>
      <NavbarAnnouncementsContext.Context
        value={{
          actions: {
            clearAnnouncement,
            selectAnnouncement,
          },
          state: {
            selectedAnnouncement,
          },
        }}
      >
        {children}
        <AnnouncementDetailDialog
          announcement={selectedAnnouncement}
          onOpenChange={(open) => {
            if (!open) {
              clearAnnouncement()
            }
          }}
        />
        <UnseenAnnouncementPanel onViewMore={selectAnnouncement} />
      </NavbarAnnouncementsContext.Context>
    </AnnouncementsMenuHintProvider>
  )
}

export function NavbarAnnouncementsHelpMenuItem() {
  const navbarAnnouncements = useNavbarAnnouncementsContext()
  return (
    <HelpAnnouncementsDropdownMenuItem
      onSelectAnnouncement={navbarAnnouncements.actions.selectAnnouncement}
    />
  )
}
